import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ModalController} from '@ionic/angular';
import {TranslocoService} from '@jsverse/transloco';
import {combineLatest, Subscription} from 'rxjs';
import {TimezoneService} from '../../../services/mygreencountry/timezone.service';
import {ParentService} from '../../../services/mygreencountry/parent.service';
import {AuthService} from '../../../services/mygreencountry/auth.service';
import {TimezoneModalComponent} from './timezone-modal.component';

@Component({
    selector: 'app-timezone-badge', standalone: true, imports: [CommonModule],
    template:     `<ng-container *ngIf="timezones.settings$ | async as settings; else pendingBadge">
        <button *ngIf="editable; else readonlyBadge" type="button" class="badge" (click)="open()"
            [disabled]="!studentId || opening" [attr.aria-label]="label">
            <span class="clock" aria-hidden="true">◷</span><span>{{timezones.offset(settings.timezone)}}<small>{{timezones.city(settings.timezone)}}</small></span>
        </button>
        <ng-template #readonlyBadge><span class="badge" [attr.aria-label]="label">
            <span class="clock" aria-hidden="true">◷</span><span>{{timezones.offset(settings.timezone)}}<small>{{timezones.city(settings.timezone)}}</small></span>
        </span></ng-template>
    </ng-container>
    <ng-template #pendingBadge>
        <span *ngIf="!editable" class="badge" [attr.aria-label]="label">
            <span class="clock" aria-hidden="true">◷</span><span>GMT —</span>
        </span>
    </ng-template>`,
    styles: [`:host { display: inline-flex; margin-left: 18px; flex-shrink: 0; } .badge { display: inline-flex; align-items: center; gap: 7px; color: #3f403f; background: #fff; border: 1px solid #cce9ae; border-radius: 3px; padding: 5px 8px; font-size: 11px; text-align: left; line-height: 1.2; } button.badge { cursor: pointer; } button:disabled { opacity: .5; } small { display: block; } .clock { color: #80b254; font-size: 22px; } @media(max-width: 600px) { :host { margin-left: 8px; } .badge { border: 0; padding: 0; font-size: 9px; gap: 3px; } small { display: none; } .clock { font-size: 18px; } }`]
})
export class TimezoneBadgeComponent implements OnInit, OnDestroy {
    @Input() editable = false;
    studentId = 0;
    opening = false;
    private subscription = new Subscription();
    private generation = 0;
    constructor(public timezones: TimezoneService, private parent: ParentService, private auth: AuthService,
        private modal: ModalController, private locale: TranslocoService) {}
    get label(): string { return this.locale.getActiveLang() === 'en' ? 'Lesson time zone' : 'Часовий пояс занять'; }
    ngOnInit(): void {
        if (!this.editable) return;

        this.subscription.add(combineLatest({
            child: this.parent.getActiveChild(),
            dashboard: this.parent.dashboardTimezone$,
            token: this.auth.userIsAuthorized$()
        }).subscribe(({child, dashboard, token}) => {
            const generation = ++this.generation;
            if (!token || !this.auth.isParentRole()) return;

            this.studentId = child?.id ?? 0;
            const settings = child && dashboard?.studentId === child.id && dashboard.token === token
                ? dashboard.settings : null;
            this.timezones.accept(settings);
            if (!settings || settings.prompted || settings.selectedTimezone ||
                this.timezones.detected() === 'Europe/Kyiv' || this.opening) return;

            const studentId = this.studentId;
            this.subscription.add(this.timezones.claim(studentId).subscribe({next: claim => {
                if (generation !== this.generation || !this.auth.isParentRole() || !claim.success) return;
                this.timezones.accept(claim.settings);
                if (claim.showPrompt) void this.open(true, studentId);
                this.parent.updateDashboardTimezone(studentId, token, claim.settings);
            }, error: () => { /* A later dashboard load can retry claiming the prompt. */ }}));
        }));
    }

    async open(automatic = false, studentId = this.studentId): Promise<void> {
        if (!this.editable || !studentId || this.opening || !this.auth.isParentRole()) return;
        this.opening = true;
        try {
            const modal = await this.modal.create({component: TimezoneModalComponent,
                componentProps: {automatic, studentId}, cssClass: automatic ? 'timezone-modal automatic-timezone-modal' : 'timezone-modal',
                backdropDismiss: false});
            await modal.present();
            const result = await modal.onDidDismiss();
            if (result.role === 'saved') window.location.reload();
        } finally { this.opening = false; }
    }
    ngOnDestroy(): void { this.generation++; this.subscription.unsubscribe(); }
}
